import { useEffect, useState } from 'react'
import type { SelectOption } from '@/components/Generic/SelectInput/SelectInput'
import { actionReport } from '../methods/actionReport'
import { axiosInstance } from '../methods/axiosConfig'
import { mapCountries } from '@/methods/mapCountries'
import { IconTypes } from '@/types/documentType'
import { setUrlParam } from '@/methods/urlParams'
import { API_URL } from '@constants/apiUrl'
import { rootStore } from '@/store/Root.store'
import { IconType } from '@/components/Generic/Icon/iconTypes'

interface DocType {
  id: number
  name: string
}

interface Country {
  id: string
  value: string
  label: string
  icon: string
  countryCode: string
  code: string
  name: string
  types: DocType[]
}

export const useDocumentType = (
  currentScenarioId: string = '',
  shouldSelectDocument: boolean = false,
  customLanguage: string = '',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trans: any
) => {
  //  Flags
  const [docType, setDocType] = useState('')
  const [country, setCountry] = useState<Country | null>(null)
  const [apiCountries, setApiCountries] = useState<Country[]>([])
  const [countryDocTypes, setCountryDocTypes] = useState<DocType[]>([])
  const [countrySelectedEventSent, setCountrySelectedEventSent] = useState<boolean>(false)
  const [btnLoading, setBtnLoading] = useState(false)

  const getDocTypes = async () => {
    try {
      const res = await axiosInstance.get(`${API_URL}/electronic-id/document-types`, {
        withCredentials: true,
      })

      res.data.documentTypes && setApiCountries(mapCountries(res.data.documentTypes))
    } catch (e) {
      throw e
    }
  }

  const handleProceed = async () => {
    if (currentScenarioId !== 'localTests') {
      setBtnLoading(true)
      setUrlParam('docType', docType)

      await axiosInstance.post(
        `${API_URL}/electronic-id/select-document-type`,
        {
          id: Number(docType),
        },
        {
          withCredentials: true,
        }
      )
      setBtnLoading(false)
    }

    rootStore.ScenarioState.nextPage()
  }

  const handleCountry = (country: SelectOption | null) => {
    if (country) {
      const countryApiObj = apiCountries.find((apiObj) => apiObj.countryCode === country.value)

      countryApiObj && setCountry(countryApiObj)
      countryApiObj && setCountryDocTypes(countryApiObj.types)
      setDocType('')
    } else {
      setCountry(null)
      setDocType('')
    }
  }

  useEffect(() => {
    if (apiCountries.length === 1) {
      handleCountry(apiCountries[0])
    } else {
      if (customLanguage.length) {
        const customCountryIndex = apiCountries.findIndex(
          (apiObj) => apiObj.countryCode === customLanguage
        )

        handleCountry(apiCountries[customCountryIndex])
      }
    }

    if (currentScenarioId === 'localTests') {
      const countryApiObj = apiCountries.find((apiObj) => apiObj.countryCode === 'PL')

      setCountry(countryApiObj || null)
      setDocType('IdentityCard')
    }
  }, [apiCountries])

  useEffect(() => {
    if (countryDocTypes.length === 1) {
      setDocType(`${countryDocTypes[0].id}`)
    }
  }, [countryDocTypes])

  const getDocLabel = (value: string) => {
    return trans[value]
  }

  const getDocIcon = (value: string): IconType => {
    const typeIcon = IconTypes.find((docType) => docType.label === value)

    return (typeIcon ? typeIcon.value : value) as IconType
  }

  useEffect(() => {
    localStorage.setItem('permRetries', '0')
    if (!shouldSelectDocument) {
      rootStore.ScenarioState.skipStep()
    } else {
      getDocTypes()
    }
  }, [])

  const changeSelectedDocumentType = (newDocType: DocType['id']) => {
    const strNewDocType = `${newDocType}`

    // Validation & safety check
    if (!strNewDocType || strNewDocType === docType) return

    setDocType(strNewDocType)

    if (!country?.value) return

    if (!countrySelectedEventSent) {
      actionReport({
        type: 'event.onboarding-web.document-selection.COUNTRY_SELECTED',
        payload: {
          country: country.value,
        },
      })

      setCountrySelectedEventSent(true)
    }

    actionReport({
      type: 'event.onboarding-web.document-selection.DOCUMENT_SELECTED',
      payload: {
        selectedDocument: strNewDocType,
        documentName: countryDocTypes.find((doc) => `${doc.id}` === strNewDocType)?.name,
      },
    })
  }

  const setSelectedCountry = (country: SelectOption | null) => {
    handleCountry(country)

    if (country) {
      actionReport({
        type: 'event.onboarding-web.document-selection.COUNTRY_SELECTED',
        payload: {
          country: country.value,
        },
      })

      setCountrySelectedEventSent(true)
    }
  }

  return {
    docType,
    setDocType,
    changeSelectedDocumentType,
    country,
    apiCountries,
    countryDocTypes,
    btnLoading,
    getDocLabel,
    getDocIcon,
    handleCountry,
    setSelectedCountry,
    handleProceed,
  }
}
